import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await register(formData);
      console.log('Registration successful:', result);
      // Delay navigation to ensure state updates are complete
      setTimeout(() => {
        navigate('/login');
      }, 300);
    } catch (error) {
      console.log('Registration failed. Error object:', error);
      console.log('Error response:', error.response);
      const errorMessage = error.response?.data?.detail || t('toast.registrationFailed');
      const translatedError = t(`auth.register.errors.${errorMessage}`) || errorMessage;
      console.log('Final error message to display:', translatedError);
      toast.error(translatedError, { autoClose: 2000 });
      // Clear the form fields after error
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t('auth.register.seoTitle') || 'Register - Create New Account'}
        description={t('auth.register.seoDescription') || 'Create a new Meller Racing account to access exclusive deals, track orders, and manage your motorcycle parts purchases.'}
        keywords="register, create account, sign up, new customer, nowy klient, rejestracja, załóż konto, Meller Racing"
        noindex={true}
      />

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-center text-3xl font-display font-bold text-gray-900">
              {t('auth.register.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.register.subtitlePrefix')} {' '}
              <Link to="/login" className="font-medium text-racing-red hover:underline">
                {t('auth.register.subtitleLink')}
              </Link>
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex justify-between items-start">
                <p className="text-sm text-red-700 flex-1">{error}</p>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0 font-bold"
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  {t('auth.register.username')}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder={t('auth.register.usernamePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('auth.register.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder={t('auth.register.emailPlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                  {t('auth.register.fullName')}
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder={t('auth.register.fullNamePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('auth.register.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder={t('auth.register.passwordPlaceholder')}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? (t('auth.register.submitting') || 'Creating account...') : t('auth.register.submit')}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Register;
